import { useTranslation } from 'react-i18next';

import type {
  TransportChainErrorResponse,
  TransportLegResponse,
  TransportSchemeTicketResponse,
} from '@/api/generated/schemas';
import { Button } from '@/shared/ui/button';
import { useMagicFormEdit } from '@/shared/legacyMagicBridge';
import { TransportModeBadge } from '../../../../components/RegularTransportSelector';
import { formatMinutes } from '../model/format';

interface AirChainTableProps {
  legs: TransportLegResponse[];
  errors: TransportChainErrorResponse[];
  ticketsById: Map<string, TransportSchemeTicketResponse>;
  /** Pokrytí legu skutečnými spoji — dorazí až druhým dotazem, do té doby `undefined`. */
  coverage: Map<string, { covered: number; total: number }>;
  readOnly: boolean;
  onRemove: (ticketId: string) => void;
}

/**
 * Tabulka legů jednoho směru. Řádky se skládají z rozřešeného řetězu — jeden řádek = jeden leg
 * = jedna nákupní jednotka v tomto směru.
 *
 * Šířky sloupců a výška řádku jsou v `transportScheme.css` (`.op-legt`), aby oba směry vedle sebe
 * měly stejnou mřížku i při různě dlouhých názvech doprav.
 */
export function AirChainTable({
  legs,
  errors,
  ticketsById,
  coverage,
  readOnly,
  onRemove,
}: AirChainTableProps) {
  const { t } = useTranslation('venua-planner');
  // Detail dopravy patří do stacku okna, ve kterém tabulka běží (osamostatněné okno ≠ plocha).
  const openFormEdit = useMagicFormEdit();

  if (legs.length === 0) {
    return <p className="op-t-empty px-2 py-3">{t('transportScheme.chain.empty')}</p>;
  }

  return (
    <div className="op-legs-wrap">
      <table className="op-legt">
        <tbody>
          {legs.map((leg, index) => {
            const error = errors.find((e) => e.index === index);
            const ticket = ticketsById.get(leg.ticketId);
            const isRoundTrip = ticket?.outboundSequence != null && ticket?.inboundSequence != null;
            const legCoverage = coverage.get(`${leg.ticketId}:${leg.facilityId}`);
            const gap = legCoverage ? legCoverage.total - legCoverage.covered : 0;

            return (
              <tr key={`${leg.ticketId}-${leg.facilityId}`} {...(error ? { 'data-err': '' } : {})}>
                <td className="seqc">
                  <span className="op-seq">{leg.sequence}</span>
                </td>

                <td className="rtc">
                  <span className="op-route">
                    {leg.fromCode ?? '—'}
                    <span className="ar">→</span>
                    {leg.toCode ?? '—'}
                  </span>
                  {error ? (
                    <span
                      className="op-err ml-1"
                      title={t(`transportScheme.chainError.${error.code}`)}
                    >
                      !
                    </span>
                  ) : null}
                </td>

                <td className="mdc">
                  {ticket ? <TransportModeBadge mode={leg.mode} t={t} /> : null}
                </td>

                <td className="namec" title={leg.regularTransportName}>
                  {leg.regularTransportName}
                </td>

                <td className="tmc">
                  {leg.departureMinutes != null && leg.arrivalMinutes != null
                    ? `${formatMinutes(leg.departureMinutes)} – ${formatMinutes(leg.arrivalMinutes)}`
                    : '—'}
                </td>

                <td className="flc">{leg.flightCode ?? '—'}</td>

                <td className="covc" {...(gap > 0 ? { 'data-warn': '' } : {})}>
                  {legCoverage
                    ? gap > 0
                      ? t('transportScheme.chain.coverageMissing', {
                          count: gap,
                          total: legCoverage.total,
                        })
                      : t('transportScheme.chain.coverage', {
                          count: legCoverage.covered,
                          total: legCoverage.total,
                        })
                    : null}
                </td>

                <td className="xc">
                  <span className="flex items-center justify-end">
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      iconLeft="external-link"
                      title={t('transportScheme.chain.openTransport')}
                      onClick={() => openFormEdit('RegularTransport', leg.regularTransportId)}
                    />
                    {!readOnly && (
                      <Button
                        size="icon-sm"
                        variant="ghost"
                        iconLeft="x"
                        className="hover:bg-phx-danger-tint hover:text-phx-danger"
                        title={t(
                          isRoundTrip
                            ? 'transportScheme.chain.removeRotation'
                            : 'transportScheme.chain.remove'
                        )}
                        onClick={() => onRemove(leg.ticketId)}
                      />
                    )}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
